import { FHService, Price, Product } from 'npx-family-happy-common';
import { BusinessLine } from 'src/app/shared/models/businessLine-model';

/** Servizio collegato all'azienda con i suoi prodotti, ognuno con i suoi prezzi. */
export interface CatalogService {
  service: CatalogFHService;
  products: CatalogProduct[];
}

export interface CatalogProduct {
  product: Product;
  prices: Price[];
}

/** Il backend restituisce anche `excluded`, che il modello comune non ha: va conservato nei salvataggi. */
export interface CatalogFHService extends FHService {
  excluded?: boolean;
}

export interface CatalogSummary {
  services: number;
  activeServices: number;
  products: number;
  prices: number;
  /** Prodotti B2B con un prezzo nella convenzione. */
  rules: number;
}

export type AreaId = 'caregiving' | 'medicina' | 'well-being';

export interface AreaMeta {
  label: string;
  /** Classe CSS del colore (vedi `_catalog.scss`). */
  tone: string;
}

/** Stesse macroaree del catalogo servizi della Web App e del load balance. */
export const AREAS: Record<AreaId, AreaMeta> = {
  caregiving: { label: 'Caregiving', tone: 'tone-caregiving' },
  medicina: { label: 'Medicina', tone: 'tone-medicina' },
  'well-being': { label: 'Well-being', tone: 'tone-wellbeing' },
};

export const AREA_ORDER: AreaId[] = ['caregiving', 'medicina', 'well-being'];

export interface LineMeta {
  label: string;
  area: AreaId;
  /** Classe bootstrap-icons. */
  icon: string;
}

export const LINES: Record<BusinessLine, LineMeta> = {
  [BusinessLine.BABYSITTER]: { label: 'Babysitter', area: 'caregiving', icon: 'bi-balloon-heart' },
  [BusinessLine.NURSE]: { label: 'Badante', area: 'caregiving', icon: 'bi-person-hearts' },
  [BusinessLine.PETSITTER]: { label: 'Petsitter', area: 'caregiving', icon: 'bi-suit-heart' },
  [BusinessLine.COLF]: { label: 'Colf', area: 'caregiving', icon: 'bi-house-heart' },
  [BusinessLine.CAREGIVER_CONSULT]: { label: 'Supporto caregiver', area: 'caregiving', icon: 'bi-chat-heart' },
  [BusinessLine.VIDEO_CONSULTATION]: { label: 'Video consulto', area: 'medicina', icon: 'bi-camera-video' },
  [BusinessLine.IN_PERSON]: { label: 'Visita medica di persona', area: 'medicina', icon: 'bi-hospital' },
  [BusinessLine.PSYCHOLOGIST]: { label: 'Psicologo', area: 'well-being', icon: 'bi-emoji-smile' },
  [BusinessLine.NUTRITIONIST]: { label: 'Nutrizionista', area: 'well-being', icon: 'bi-egg-fried' },
  [BusinessLine.COACH]: { label: 'Coach', area: 'well-being', icon: 'bi-bullseye' },
  [BusinessLine.LONGEVITY]: { label: 'Longevity', area: 'well-being', icon: 'bi-hourglass-split' },
};

/** Linee nell'ordine delle macroaree, per le select. */
export const LINE_OPTIONS = AREA_ORDER.map((area) => ({
  area,
  label: AREAS[area].label,
  lines: (Object.keys(LINES) as BusinessLine[]).filter((key) => LINES[key].area === area),
}));

export function lineOf(businessLine: string): LineMeta {
  return LINES[businessLine as BusinessLine] ?? { label: businessLine, area: 'caregiving', icon: 'bi-circle' };
}

export const CUSTOMER_TYPES = ['B2B', 'B2C', 'CAREGIVER', 'UNICREDIT'];

/** Valori dell'enum ProductType del backend (il modello comune ha solo i primi due). */
export const PRODUCT_TYPES: { value: string; label: string }[] = [
  { value: 'SERVICE', label: 'Una tantum' },
  { value: 'SUBSCRIPTION', label: 'Abbonamento mensile' },
  { value: 'SUBSCRIPTION3', label: 'Abbonamento 3 mesi' },
  { value: 'SUBSCRIPTION12', label: 'Abbonamento 12 mesi' },
];

export function productTypeLabel(type: string): string {
  return PRODUCT_TYPES.find((t) => t.value === type)?.label ?? type;
}

/** Stesse province di app-province-select / app-province-filter. */
export const PROVINCES: { value: string; label: string }[] = [
  { value: 'TO', label: 'Torino' },
  { value: 'MI', label: 'Milano' },
  { value: 'RM', label: 'Roma' },
  { value: 'BO', label: 'Bologna' },
  { value: 'PD', label: 'Padova' },
  { value: 'GE', label: 'Genova' },
  { value: 'FI', label: 'Firenze' },
  { value: 'BS', label: 'Brescia' },
  { value: 'BG', label: 'Bergamo' },
  { value: 'MB', label: 'Monza' },
  { value: 'VR', label: 'Altre province' },
];

export function provinceLabel(value: string): string {
  return PROVINCES.find((p) => p.value === value)?.label ?? value;
}

/** Prezzi uguali (stesso importo e stesso Stripe ID) raggruppati: nel listino di solito cambia solo la provincia. */
export interface PriceGroup {
  amount: number;
  stripeID: string;
  provinces: string[];
}

export function groupPrices(prices: Price[]): PriceGroup[] {
  const groups: PriceGroup[] = [];
  prices.forEach((price) => {
    const group = groups.find((g) => g.amount === price.amount && g.stripeID === price.stripeID);
    if (group) group.provinces.push(price.province);
    else groups.push({ amount: price.amount, stripeID: price.stripeID, provinces: [price.province] });
  });
  return groups.sort((a, b) => a.amount - b.amount);
}

/** Gli importi arrivano in centesimi. */
export function euro(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

/**
 * Messaggio d'errore con il motivo del backend: il `message` del corpo se c'è, altrimenti il corpo stesso
 * (es. errori di validazione per campo), e solo in ultima istanza il messaggio HTTP generico.
 */
export function errorMessage(error: unknown, fallback: string): string {
  const detail = backendDetail((error as any)?.error) ?? (error as any)?.message;
  return detail ? `${fallback} (${detail})` : fallback;
}

function backendDetail(body: unknown): string | null {
  if (typeof body === 'string') return body.trim() || null;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (typeof b['message'] === 'string' && b['message']) return b['message'];
  if (typeof b['detail'] === 'string' && b['detail']) return b['detail'];
  if (b['errors']) return JSON.stringify(b['errors']);
  // Senza un messaggio esplicito si mostra il corpo, senza i campi che non dicono nulla.
  const { timestamp, path, status, ...rest } = b;
  const text = JSON.stringify(rest);
  return text === '{}' ? null : text.length > 300 ? `${text.slice(0, 300)}…` : text;
}
